import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Mail, Phone, Menu, X, Facebook, Instagram, Youtube, MessageCircle } from 'lucide-react';
import { SITE, NAV_LINKS } from '../data/mock';

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="w-full sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="hidden md:flex bg-white border-b border-slate-100 text-sm">
        <div className="max-w-[1400px] mx-auto w-full flex items-stretch justify-between pl-6">
          <div className="flex items-center gap-6 text-slate-500">
            <span>Welcome to Moleboheng Matli Foundation</span>
          </div>
          <div className="flex items-stretch">
            <div className="flex items-center gap-4 px-5 text-slate-500">
              <a href="https://www.facebook.com/share/1BeHD47JMp/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[#F97559] transition-colors"><Facebook size={16} /></a>
              <a href="https://www.instagram.com/molebohengmatlifoundation?igsh=OTRlbTV2dDU1cWRx&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#F97559] transition-colors"><Instagram size={16} /></a>
              <a href="https://youtube.com/@molebohengmatlifoundationtv?si=a3IgB35-O_Q8q7os" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-[#F97559] transition-colors"><Youtube size={16} /></a>
              <a href={SITE.whatsappChannel} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp Channel" className="hover:text-[#25D366] transition-colors"><MessageCircle size={16} /></a>
            </div>
            <a href="https://pay.yoco.com/moleboheng-matli-foundation" target="_blank" rel="noopener noreferrer" className="bg-[#2BBECF] hover:bg-[#1ca6b6] text-white px-8 flex items-center font-semibold transition-colors">
              Donate
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`bg-white transition-shadow ${scrolled ? 'shadow-md' : 'shadow-sm'}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between gap-6 xl:gap-10 h-16 md:h-20 lg:h-22">
          <Link to="/" className="flex items-center shrink-0">
            <img src={SITE.logo} alt={SITE.name} className="h-12 md:h-14 lg:h-16 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 flex-1 justify-center">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `nav-link text-[14px] font-semibold tracking-wide ${isActive ? 'active text-slate-900' : 'text-slate-700 hover:text-slate-900'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-4 shrink-0 pl-6 border-l border-slate-200">
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2.5 group" title={SITE.email}>
              <span className="w-9 h-9 rounded-full bg-[#F97559] grid place-items-center text-white shrink-0 group-hover:scale-105 transition-transform">
                <Mail size={14} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Email Us</span>
                <span className="text-[12px] text-slate-700 group-hover:text-[#F97559] transition-colors font-semibold">info@molebohengmatlifoundation.org.za</span>
              </span>
            </a>
            <a href={`tel:${SITE.phone}`} className="flex items-center gap-2.5 group">
              <span className="w-9 h-9 rounded-full bg-[#F97559] grid place-items-center text-white shrink-0 group-hover:scale-105 transition-transform">
                <Phone size={14} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Call Us</span>
                <span className="text-[12px] text-slate-700 group-hover:text-[#F97559] transition-colors font-semibold">{SITE.phone}</span>
              </span>
            </a>
          </div>

          <button onClick={() => setOpen(!open)} className="lg:hidden p-2" aria-label="Toggle menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile nav */}
        {open && (
          <div className="lg:hidden border-t border-slate-100 bg-white">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.to === '/'}
                  className={({ isActive }) =>
                    `py-2 text-base font-semibold ${isActive ? 'text-[#F97559]' : 'text-slate-700'}`
                  }
                >{l.label}</NavLink>
              ))}
              <a href={`mailto:${SITE.email}`} className="py-2 text-sm text-slate-600 flex items-center gap-2"><Mail size={14} />{SITE.email}</a>
              <a href={`tel:${SITE.phone}`} className="py-2 text-sm text-slate-600 flex items-center gap-2"><Phone size={14} />{SITE.phone}</a>
              <a href="https://pay.yoco.com/moleboheng-matli-foundation" target="_blank" rel="noopener noreferrer" className="mt-2 bg-[#2BBECF] hover:bg-[#1ca6b6] text-white px-5 py-2.5 rounded text-center font-semibold">Donate</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
